/**
 * GP-1431 — where a split Order History row CAME FROM.
 *
 * Patient 360 → Order & Subscription History → Order History lists a split
 * family as peers. London patient `…_29033` showed three rows for one
 * subscription — `ORD-20260813-204516-603`, a struck-through
 * `TS-ORD-20260813-204516-603-1786654030834` and
 * `CC-TS-ORD-20260813-204516-603-1786654030834` — each with its own date and
 * stage and nothing but an amber `Split · N items` chip between them. The chip
 * said "these ship together"; it never said which order this row was carved out
 * of, why, or when. The reporter had to open both detail views and scroll a
 * 20-event timeline to find the `Cold Chain Split` line that names the parent.
 *
 * The link was never missing from the data — every split path writes an
 * immediate-parent pointer plus a reason into `order.metadata`, and the durable
 * `splitFromOrderId` column carries the same parent id. This module reads it back
 * so the row can state it inline.
 *
 * No inference from the order NUMBER: `CC-TS-ORD-<parent>-<ts>` looks parseable,
 * but a name is not a relationship — a renamed or hand-entered order number would
 * fabricate a parent link. Only recorded metadata counts, and a row with no
 * recorded provenance returns null rather than a guess.
 */

export type SplitKind = 'cold_chain' | 'item_removed' | 'balance_owing' | 'reprocess';

export interface SplitProvenance {
  kind: SplitKind;
  /** Short chip label, e.g. "Cold chain split". */
  label: string;
  /** What this split did, for the tooltip. Never speculative. */
  description: string;
  /** Immediate parent — the order this row was carved out of. */
  parentOrderId: string | null;
  parentOrderNumber: string | null;
  /** ISO timestamp of the split, when the writing path recorded one. */
  splitAt: string | null;
  /**
   * Whoever performed the split, exactly as stored (usually an Auth0 subject).
   * Callers resolve it to a display name — see `useBatchUserLookup`.
   */
  splitByUserId: string | null;
  /** Operator-entered reason, when the split path collected one. */
  reason: string | null;
}

interface SplitSource {
  kind: SplitKind;
  /** `order.metadata` key holding this split's record. */
  metadataKey: string;
  label: string;
  description: string;
  parentIdKeys: string[];
  parentNumberKeys: string[];
  timestampKeys: string[];
  actorKeys: string[];
  reasonKeys: string[];
}

/**
 * One entry per split path in the backend. Keys mirror exactly what each writer
 * persists — see `OrdersService.performColdChainSplit` (`coldChainSplit`),
 * `OrdersService.removeItemToTroubleshooting` (`itemRemoval`),
 * `PackagingController` partial fill (`balanceOwing`) and
 * `OrderReprocessService` (`reprocess`).
 */
const SPLIT_SOURCES: SplitSource[] = [
  {
    kind: 'cold_chain',
    metadataKey: 'coldChainSplit',
    label: 'Cold chain split',
    description: 'Refrigerated items were moved onto this order so they travel separately.',
    parentIdKeys: ['parentOrderId'],
    parentNumberKeys: ['parentOrderNumber'],
    timestampKeys: ['splitAt'],
    actorKeys: ['splitBy'],
    reasonKeys: [],
  },
  {
    kind: 'item_removed',
    metadataKey: 'itemRemoval',
    label: 'Troubleshooting split',
    description: 'A drug was removed from the parent order and sent to troubleshooting on its own.',
    parentIdKeys: ['parentOrderId'],
    parentNumberKeys: ['parentOrderNumber'],
    timestampKeys: ['removedAt'],
    actorKeys: ['removedBy'],
    reasonKeys: ['reason'],
  },
  {
    kind: 'balance_owing',
    metadataKey: 'balanceOwing',
    label: 'Balance owing split',
    description: 'The quantity the parent order could not fill was carried onto this order.',
    parentIdKeys: ['parentOrderId'],
    parentNumberKeys: ['parentOrderNumber'],
    timestampKeys: ['createdAt', 'lastMergedAt'],
    actorKeys: [],
    reasonKeys: ['reason'],
  },
  {
    kind: 'reprocess',
    metadataKey: 'reprocess',
    label: 'Reprocess replacement',
    description: 'This order replaces one that had to be re-sent.',
    parentIdKeys: ['sourceOrderId'],
    parentNumberKeys: ['sourceOrderNumber'],
    timestampKeys: ['reprocessedAt'],
    actorKeys: ['reprocessedBy'],
    reasonKeys: ['reasonLabel', 'reason'],
  },
];

const firstString = (record: any, keys: string[]): string | null => {
  for (const key of keys) {
    const value = record?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

const toTime = (iso: string | null): number => {
  if (!iso) return 0;
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * The split this row is the CHILD of, or null when the row records none.
 *
 * A cold-chain PARENT also carries a `coldChainSplit` record, but it stores the
 * CHILD's id (`coldChainOrderId`) — requiring a parent pointer is what keeps the
 * parent row unmarked.
 *
 * A row can carry more than one record: `CC-TS-ORD-…` inherits the TS order's
 * `itemRemoval` (which points at the ORIGINAL order) and then gains its own
 * `coldChainSplit` (which points at the TS order). The immediate parent is the
 * one whose id matches the durable `splitFromOrderId` column; failing that (older
 * rows where the column was cleared on merge), the most recent record wins.
 */
export const getSplitProvenance = (order: any): SplitProvenance | null => {
  const metadata = order?.metadata;
  if (!metadata || typeof metadata !== 'object') return null;

  const candidates: SplitProvenance[] = [];
  for (const source of SPLIT_SOURCES) {
    const record = (metadata as any)[source.metadataKey];
    if (!record || typeof record !== 'object') continue;

    const parentOrderId = firstString(record, source.parentIdKeys);
    const parentOrderNumber = firstString(record, source.parentNumberKeys);
    // No parent pointer → this is not a child record (see the cold-chain parent
    // note above). Never fall back to parsing the order number.
    if (!parentOrderId && !parentOrderNumber) continue;
    // A record pointing at the row itself is drift, not a parent link.
    if (parentOrderId && order?.id && parentOrderId === order.id) continue;

    candidates.push({
      kind: source.kind,
      label: source.label,
      description: source.description,
      parentOrderId,
      parentOrderNumber,
      splitAt: firstString(record, source.timestampKeys),
      splitByUserId: firstString(record, source.actorKeys),
      reason: firstString(record, source.reasonKeys),
    });
  }

  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  const immediate = order?.splitFromOrderId
    ? candidates.find((c) => c.parentOrderId === order.splitFromOrderId)
    : undefined;
  if (immediate) return immediate;

  return [...candidates].sort((a, b) => toTime(b.splitAt) - toTime(a.splitAt))[0];
};

/**
 * One-line sentence for the row, e.g.
 * "Cold chain split from TS-ORD-20260813-204516-603-1786654030834".
 * The parent number is preferred; an id-only record says so rather than printing
 * a UUID as if it were an order number.
 */
export const formatSplitOrigin = (provenance: SplitProvenance): string => {
  const parent = provenance.parentOrderNumber;
  return parent
    ? `${provenance.label} from ${parent}`
    : `${provenance.label} from another order`;
};
